<template>
  <div
    class="page-wrapper page-wrapper--body-scroll dashboard-page"
    :class="{ 'dashboard-page--usage-fixed': activeTab === 'agent-call-usage' }"
    v-loading="loading"
  >
    <div class="panel-intro">
      <div class="panel-heading">
        <h3 class="panel-title">首页</h3>
        <el-tabs v-model="activeTab" class="dashboard-tabs">
          <el-tab-pane label="小程序总览" name="one" />
          <el-tab-pane label="Agent 反馈总览" name="agent-feedback" />
          <el-tab-pane label="调用消耗" name="agent-call-usage" />
          <el-tab-pane label="API 额度管理" name="api-quota" />
          <el-tab-pane label="自定义" name="two" />
        </el-tabs>
      </div>

      <div class="panel-body">
        <div v-show="activeTab === 'one'">
          <el-row :gutter="20" class="stat-row">
            <el-col v-for="item in topStats" :key="item.key" :xs="12" :sm="6">
              <div class="sm-st clearfix">
                <span class="sm-st-icon" :class="item.iconClass">{{ item.icon }}</span>
                <div class="sm-st-info">
                  <span>{{ item.value }}</span>
                  {{ item.label }}
                </div>
              </div>
            </el-col>
          </el-row>

          <el-row :gutter="20" class="chart-row">
            <el-col :xs="24">
              <div ref="chartRef" class="echart-box" />
            </el-col>
          </el-row>

          <el-row :gutter="20" class="stats-row">
            <el-col :xs="24">
              <div class="stats-card">
                <el-row :gutter="12">
                  <el-col
                    v-for="item in sideStats"
                    :key="item.key"
                    :xs="12"
                    :sm="8"
                    :md="6"
                    class="stat-col"
                  >
                    <div class="stat-icon">{{ item.icon }}</div>
                    <div class="stat">
                      <div class="value">{{ item.value }}</div>
                      <div class="name" :title="item.tips">{{ item.label }}</div>
                      <small v-if="item.sub" class="stat-sub">{{ item.sub }}</small>
                    </div>
                    <el-progress :percentage="item.progress" :show-text="false" :stroke-width="2" />
                  </el-col>
                </el-row>
              </div>
            </el-col>
          </el-row>

          <el-row :gutter="20" class="bottom-row">
            <el-col v-for="panel in bottomPanels" :key="panel.key" :xs="12" :md="6">
              <div class="gradient-panel" :class="panel.bgClass">
                <div class="gradient-panel__head">
                  <span class="label-tag">实时</span>
                  <h5>{{ panel.title }}</h5>
                </div>
                <div class="gradient-panel__body">
                  <el-row>
                    <el-col
                      v-for="(metric, idx) in panel.metrics"
                      :key="idx"
                      :span="panel.metrics.length > 1 ? 12 : 24"
                    >
                      <h1 class="no-margins">{{ metric.value }}</h1>
                      <div class="font-bold">
                        <span>{{ metric.icon }}</span>
                        <small>{{ metric.label }}</small>
                      </div>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <div v-if="activeTab === 'agent-feedback'">
          <AgentFeedbackOverview />
        </div>

        <div v-if="activeTab === 'agent-call-usage'" class="usage-tab-pane">
          <AgentCallUsageOverview />
        </div>

        <div v-if="activeTab === 'api-quota'">
          <ApiQuotaOverview />
        </div>

        <div v-show="activeTab === 'two'" class="custom-zone">这里是你的自定义数据</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import axios from 'axios'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import AgentFeedbackOverview from '@/components/AgentFeedbackOverview.vue'
import AgentCallUsageOverview from '@/components/AgentCallUsageOverview.vue'
import ApiQuotaOverview from '@/components/ApiQuotaOverview.vue'

const activeTab = ref('one')
const loading = ref(false)
const chartRef = ref(null)
let chartInstance = null

const emptyMetrics = () => ({ total_uv: 0, login_uv: 0, login_rate: 0 })

const dashboard = ref({
  totaluser: 0,
  totaladmin: 0,
  totalcategory: 0,
  todayusersignup: 0,
  todayuserlogin: 0,
  threednu: 0,
  sevendnu: 0,
  sevendau: 0,
  thirtydau: 0,
  dbtablenums: 0,
  dbsize: 0,
  wxlogintoday: emptyMetrics(),
  wxloginseven: emptyMetrics(),
  wxloginthirty: emptyMetrics(),
  column: [],
  userdata: [],
})

function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1)
  const val = n / 1024 ** i
  return `${val.toFixed(i > 0 ? 2 : 0)} ${units[i]}`
}

const topStats = computed(() => {
  const d = dashboard.value
  return [
    { key: 'totaluser', value: d.totaluser, label: '总会员数', icon: '👥', iconClass: 'st-red' },
    { key: 'totaladmin', value: d.totaladmin, label: '总管理员数', icon: '👤', iconClass: 'st-green' },
    { key: 'totalcategory', value: d.totalcategory, label: '总分类数', icon: '�', iconClass: 'st-blue' },
  ]
})

const sideStats = computed(() => {
  const d = dashboard.value
  const rateItem = (key, label, tips, data) => ({
    key,
    label,
    tips,
    icon: key.includes('today') ? '🔑' : key.includes('seven') ? '📈' : '📊',
    value: `${data?.login_rate ?? 0}%`,
    sub: `${data?.login_uv ?? 0}/${data?.total_uv ?? 0}`,
    progress: Math.min(100, Number(data?.login_rate) || 0),
  })
  return [
    { key: 'todayusersignup', label: '今日注册', icon: '🚀', value: d.todayusersignup, progress: 20 },
    { key: 'todayuserlogin', label: '今日活跃', icon: '📇', value: d.todayuserlogin, progress: 20 },
    { key: 'threednu', label: '三日新增', icon: '📅', value: d.threednu, progress: 20 },
    { key: 'sevendnu', label: '七日新增', icon: '🗓', value: d.sevendnu, progress: 20 },
    { key: 'sevendau', label: '七日活跃', icon: '🙂', value: d.sevendau, progress: 20 },
    { key: 'thirtydau', label: '月活跃', icon: '🧑', value: d.thirtydau, progress: 20 },
    rateItem('wxlogintoday', '今日登录率', '小程序埋点：登录访客/全部访客', d.wxlogintoday),
    rateItem('wxloginseven', '七日登录率', '小程序埋点：登录访客/全部访客', d.wxloginseven),
    rateItem('wxloginthirty', '月登录率', '小程序埋点：登录访客/全部访客', d.wxloginthirty),
  ]
})

const bottomPanels = computed(() => {
  const d = dashboard.value
  return [
    {
      key: 'database',
      title: '数据库统计',
      bgClass: 'bg-teal-gradient',
      metrics: [
        { value: d.dbtablenums, icon: '🗄', label: '数据表数量' },
        { value: formatBytes(d.dbsize), icon: '💾', label: '占用空间' },
      ],
    },
  ]
})

function applyDashboardData(raw = {}) {
  dashboard.value = {
    ...dashboard.value,
    ...raw,
    wxlogintoday: { ...emptyMetrics(), ...(raw.wxlogintoday || {}) },
    wxloginseven: { ...emptyMetrics(), ...(raw.wxloginseven || {}) },
    wxloginthirty: { ...emptyMetrics(), ...(raw.wxloginthirty || {}) },
    column: raw.column || [],
    userdata: raw.userdata || [],
  }
}

async function loadDashboard() {
  loading.value = true
  try {
    const res = await axios.get('/api/dashboard')
    const payload = res.data?.data ?? res.data ?? {}
    applyDashboardData(payload)
    await nextTick()
    renderChart()
  } catch (e) {
    ElMessage.error(e?.response?.data?.msg || '加载小程序总览数据失败')
  } finally {
    loading.value = false
  }
}

function renderChart() {
  if (!chartRef.value) return
  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value)
  }
  const d = dashboard.value
  chartInstance.setOption({
    color: ['#18d1b1', '#3fb1e3', '#626c91', '#a0a7e6', '#c4ebad', '#96dee8'],
    tooltip: { trigger: 'axis' },
    legend: { data: ['注册用户数'] },
    grid: { left: 40, right: 20, top: 40, bottom: 30 },
    xAxis: { type: 'category', boundaryGap: false, data: d.column },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{
      name: '注册用户数',
      type: 'line',
      smooth: true,
      areaStyle: {},
      lineStyle: { width: 1.5 },
      data: d.userdata,
    }],
  }, true)
  chartInstance.resize()
}

function handleResize() {
  chartInstance?.resize()
}

onMounted(async () => {
  await loadDashboard()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  chartInstance?.dispose()
  chartInstance = null
})
</script>

<style scoped>
.dashboard-page {
  flex: 1;
  min-height: 0;
  overflow: auto;
  box-sizing: border-box;
}

.dashboard-page--usage-fixed {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dashboard-page--usage-fixed .panel-intro {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dashboard-page--usage-fixed .panel-heading {
  flex-shrink: 0;
}

.dashboard-page--usage-fixed .panel-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dashboard-page--usage-fixed .usage-tab-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-intro {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.panel-heading {
  padding: 12px 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.panel-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.dashboard-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
}

.panel-body {
  padding: 16px;
}

.sm-st {
  background: #fff;
  padding: 20px;
  border-radius: 3px;
  margin-bottom: 20px;
  border: 1px solid #f0f0f0;
}

.sm-st-icon {
  width: 60px;
  height: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  border-radius: 5px;
  float: left;
  margin-right: 10px;
  color: #fff;
}

.sm-st-info {
  padding-top: 2px;
  overflow: hidden;
}

.sm-st-info span {
  display: block;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.st-red { background: #f05050; }
.st-violet { background: #7266ba; }
.st-blue { background: #23b7e5; }
.st-green { background: #27c24c; }

.echart-box {
  height: 300px;
  width: 100%;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.stats-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 12px;
  min-height: 300px;
  box-sizing: border-box;
}

.stat-col {
  margin-bottom: 12px;
}

.stat-icon {
  font-size: 20px;
  float: left;
  width: 32px;
  text-align: center;
}

.stat {
  overflow: hidden;
  margin-left: 32px;
  margin-bottom: 6px;
}

.stat .value {
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
}

.stat .name {
  margin: 4px 0;
  font-size: 13px;
  color: #666;
}

.stat-sub {
  display: block;
  font-size: 11px;
  color: #888;
}

.gradient-panel {
  color: #fff;
  border-radius: 4px;
  margin-bottom: 16px;
  min-height: 150px;
}

.gradient-panel__head {
  padding: 14px 16px 0;
}

.gradient-panel__head h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.label-tag {
  float: right;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  padding: 2px 8px;
  font-size: 12px;
}

.gradient-panel__body {
  padding: 8px 16px 16px;
}

.no-margins {
  margin: 8px 0;
  font-size: 28px;
  font-weight: 600;
}

.font-bold {
  font-size: 12px;
  opacity: 0.95;
}

.font-bold small {
  margin-left: 4px;
}

.bg-blue-gradient { background: linear-gradient(135deg, #2f80ed, #56ccf2); }
.bg-teal-gradient { background: linear-gradient(135deg, #11998e, #38ef7d); }
.bg-purple-gradient { background: linear-gradient(135deg, #7f00ff, #e100ff); }
.bg-green-gradient { background: linear-gradient(135deg, #0ba360, #3cba92); }

.custom-zone {
  padding: 24px;
  color: #666;
}

.clearfix::after {
  content: '';
  display: block;
  clear: both;
}
</style>
